const SENSITIVE_QUERY_TERMS = [
  'token',
  'code',
  'secret',
  'password',
  'auth',
  'handoff',
  'portaltoken',
  'session',
  'email',
  'phone',
  'key',
  'jwt',
];

const MAX_QUERY_VALUE_LENGTH = 120;
const REDACTED_VALUE = '[redacted]';

function normalizeKey(value) {
  return String(value || '').trim();
}

function isSensitiveQueryKey(key) {
  const normalized = normalizeKey(key).toLowerCase().replace(/[^a-z0-9]/g, '');
  return SENSITIVE_QUERY_TERMS.some((term) => normalized.includes(term));
}

function truncateQueryValue(value) {
  const stringValue = String(value ?? '');
  return stringValue.length > MAX_QUERY_VALUE_LENGTH
    ? `${stringValue.slice(0, MAX_QUERY_VALUE_LENGTH - 3)}...`
    : stringValue;
}

function appendQueryValue(target, key, value) {
  if (Object.prototype.hasOwnProperty.call(target, key)) {
    const current = target[key];
    target[key] = Array.isArray(current) ? [...current, value] : [current, value];
    return;
  }

  target[key] = value;
}

export function sanitizePageViewQuery(search = '') {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const query = {};
  const queryKeys = [];
  const sanitizedParams = new URLSearchParams();

  params.forEach((value, key) => {
    if (!queryKeys.includes(key)) {
      queryKeys.push(key);
    }

    const sanitizedValue = isSensitiveQueryKey(key) ? REDACTED_VALUE : truncateQueryValue(value);
    appendQueryValue(query, key, sanitizedValue);
    sanitizedParams.append(key, sanitizedValue);
  });

  const sanitizedSearch = sanitizedParams.toString();

  return {
    query,
    queryKeys,
    sanitizedSearch: sanitizedSearch ? `?${sanitizedSearch}` : '',
  };
}

export function describePageViewHash(hash = '') {
  const normalized = String(hash || '');
  if (!normalized) return null;

  return {
    present: true,
    length: normalized.length,
  };
}

export function buildSanitizedPagePath({ pathname = '/', search = '', hash = '' } = {}) {
  const { sanitizedSearch } = sanitizePageViewQuery(search);
  const hashSuffix = hash ? '#[redacted]' : '';
  return `${pathname || '/'}${sanitizedSearch}${hashSuffix}`;
}

export function buildPageViewMetadata({
  pathname = '/',
  search = '',
  hash = '',
  referrer = '',
  title = '',
  navigationKind = 'initial',
  previousPath = null,
} = {}) {
  const { query, queryKeys, sanitizedSearch } = sanitizePageViewQuery(search);
  const pathWithSearch = `${pathname || '/'}${sanitizedSearch}`;

  return {
    route: {
      pathname: pathname || '/',
      pathWithSearch,
      hash: describePageViewHash(hash),
      query,
      queryKeys,
      referrer: referrer || null,
      title: title || null,
    },
    navigation: {
      kind: navigationKind === 'route_change' ? 'route_change' : 'initial',
      ...(previousPath ? { previousPath } : {}),
    },
  };
}

export const PAGE_VIEW_REDACTED_VALUE = REDACTED_VALUE;
